import moment from "moment";
import { useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Button from "../../../components/Button";
import {
  BillingAction,
  ClientBilling,
  CollectionMethod,
  STATUS_COLORS,
  STATUS_LABELS,
} from "../types";
import { formatCurrencyPrecise } from "../utils";
import { BillingMethodPicker } from "./charge-preview/BillingMethodPicker";
import { InvoiceLinesPreview } from "./charge-preview/InvoiceLinesPreview";
import { IssuedInvoicePanel } from "./charge-preview/IssuedInvoicePanel";
import { ChargeAuditPanel } from "./charge-preview/ChargeAuditPanel";

const DEFAULT_INVOICE_DUE_DAYS = 30;
// Mirrors SCHEDULED_CHARGE_NOTICE_DAYS in the backend's billing.constants.ts.
const DEFAULT_NOTICE_DAYS = 5;

/** Bill now: POST /admin/commission-billing/charge. */
export type ChargeCardPayload = {
  action: "charge_card";
  customAmount?: number;
  invoiceNote?: string;
  acknowledgeRateMismatch?: boolean;
  sendInvoiceEmail?: boolean;
  collectionMethod: CollectionMethod;
  daysUntilDue?: number;
};

/**
 * Email the invoice as an advance notice and take the card later: POST
 * /admin/commission-billing/schedule-invoice-notice. Carries no amount, note or
 * collection method — that endpoint prices the period itself from the same
 * preview and always bills the card on file, so passing them would be a lie.
 */
export type ScheduleNoticePayload = {
  action: "schedule_notice";
  noticeDays: number;
};

/** Everything the admin decided in the modal, handed back on confirm. */
export type ChargeConfirmPayload = ChargeCardPayload | ScheduleNoticePayload;

type ChargePreviewModalProps = {
  client: ClientBilling;
  month: string;
  onClose: () => void;
  onConfirm: (payload: ChargeConfirmPayload) => void;
  // Passed the admin's live billing choice so the preview matches what will send.
  onPreviewInvoiceEmail?: (opts: {
    collectionMethod: CollectionMethod;
    daysUntilDue?: number;
  }) => void;
  /** Copies the issued invoice's Stripe pay link (shown once the bill exists). */
  onCopyPayLink?: () => void;
  isLoading?: boolean;
};

export function ChargePreviewModal({
  client,
  month,
  onClose,
  onConfirm,
  onPreviewInvoiceEmail,
  onCopyPayLink,
  isLoading = false,
}: ChargePreviewModalProps) {
  const monthLabel = moment(month, "YYYY-MM").format("MMMM YYYY");
  const pm = client.paymentMethod;

  // Check if this is "view details" mode (already billed) vs "charge" mode
  const isViewMode =
    client.invoiceStatus === "charged" ||
    client.invoiceStatus === "paid" ||
    client.invoiceStatus === "invoiced";

  // State for editable charge amount - use string for better input handling.
  // In view mode, use totalAmount from the charge record (the actual billed amount).
  // In charge mode, seed from the server-authoritative totalChargeAmount (which
  // already includes the email-marketing add-on and equals sum(lineItems)); only
  // fall back to the client-side commission+platformFee derivation when the
  // server total is absent (e.g. a preview that predates Phase 3).
  const initialAmount = (() => {
    if (isViewMode && client.totalAmount != null) return client.totalAmount;
    if (client.totalChargeAmount != null) return client.totalChargeAmount;
    const commission = client.commission || 0;
    const platformFee = client.platformFeeAmount || 0;
    if (client.billingType === "hybrid") return commission + platformFee;
    if (client.billingType === "fixed") return platformFee > 0 ? platformFee : commission;
    return commission;
  })();
  const [editableAmountStr, setEditableAmountStr] = useState(
    initialAmount.toString()
  );
  const [isEditing, setIsEditing] = useState(false);
  const [invoiceNote, setInvoiceNote] = useState("");
  const [acknowledgeRateMismatch, setAcknowledgeRateMismatch] = useState(false);

  // Auto-charge needs a usable card; a payable Stripe invoice only needs the
  // linked customer (they pay on Stripe's hosted page). The advance notice ends
  // in a card charge a few days later, so it needs everything a card charge
  // needs — a Stripe customer alone is not enough to promise a date.
  const canAutoCharge = Boolean(pm && client.stripeCustomerId);
  const canSendInvoice = Boolean(client.stripeCustomerId);
  const canScheduleNotice = canAutoCharge;
  // Seeded once at mount, like the collection method below: the customer's
  // standing preference preselects the notice, and the admin's choice then wins
  // for the rest of the modal's life.
  const [action, setAction] = useState<BillingAction>(
    canScheduleNotice && client.prefersEmailInvoice
      ? "schedule_notice"
      : "charge_card",
  );
  const [collectionMethod, setCollectionMethod] = useState<CollectionMethod>(
    canAutoCharge ? "charge_automatically" : "send_invoice",
  );
  const isScheduleNotice = action === "schedule_notice";
  const isSendInvoice =
    action === "charge_card" && collectionMethod === "send_invoice";
  const [dueDaysStr, setDueDaysStr] = useState(
    String(DEFAULT_INVOICE_DUE_DAYS),
  );
  const dueDays = parseInt(dueDaysStr, 10);
  const isDueDaysValid =
    Number.isInteger(dueDays) && dueDays >= 1 && dueDays <= 90;
  const [noticeDaysStr, setNoticeDaysStr] = useState(
    String(DEFAULT_NOTICE_DAYS),
  );
  const noticeDays = parseInt(noticeDaysStr, 10);
  const isNoticeDaysValid =
    Number.isInteger(noticeDays) && noticeDays >= 1 && noticeDays <= 90;

  // Card mode: off by default — the customer is charged but NOT emailed an
  // invoice unless the admin opts in. Invoice mode flips it on (the email
  // carries the payment link). Maps to ChargeClientDto.sendInvoiceEmail.
  const [sendInvoiceEmail, setSendInvoiceEmail] = useState(!canAutoCharge);

  const selectBillingMethod = (
    nextAction: BillingAction,
    method: CollectionMethod,
  ) => {
    setAction(nextAction);
    setCollectionMethod(method);
    // Re-seed the email opt-in to the mode's default; the admin can still
    // override it afterwards. The notice IS an email, so the opt-in is not
    // offered there and its value never reaches that payload.
    setSendInvoiceEmail(method === "send_invoice");
    if (nextAction === "schedule_notice") {
      // The notice endpoint takes no amount — it prices the period itself. Drop
      // any in-progress override rather than leave a typed figure on screen
      // that would be silently ignored.
      setIsEditing(false);
      setEditableAmountStr(initialAmount.toString());
      setAmountWasEdited(false);
    }
  };
  // A charge is a custom-amount override ONLY when the admin actually edits the
  // amount. The field is pre-seeded from the server total, so sending it
  // unconditionally stamped every charge is_custom_amount=true (a false "Manual
  // override" badge) and double-billed the add-on (the backend adds it on top of
  // customAmount). Track a deliberate edit and only send customAmount then.
  const [amountWasEdited, setAmountWasEdited] = useState(false);

  const editableAmount = parseFloat(editableAmountStr) || 0;

  // The charged commission rate differs from the rate the client's plan declares.
  // The backend blocks the charge until the admin acknowledges the override.
  const hasRateMismatch = !isViewMode && client.planRateMismatch === true;

  // Invoice line items come from the backend preview endpoint, which uses
  // the exact same helper (buildCommissionInvoiceLines) that the real charge
  // flow uses. Never recompute these client-side — the preview and the real
  // Stripe invoice are now guaranteed consistent.
  const invoiceLines = client.lineItems || [];

  // The add-on amount comes from the preview's add-on line (authoritative — it
  // reflects the server-side flag gate, not just the per-client config). The
  // backend charges customAmount + addOnAmount, so when the admin edits the
  // amount with the add-on enabled, the typed value is the fee only and the
  // add-on rides on top. Surface that so the admin isn't surprised the bill is
  // higher than the number they typed.
  const addOnLine = invoiceLines.find((line) =>
    line.description.startsWith("Email Marketing Add-On"),
  );
  const addOnAmount = addOnLine?.amount ?? 0;
  const hasAddOn = addOnAmount > 0;

  // Card mode is allowed when the backend says canCharge, or when the admin
  // entered a custom amount > 0 and a card + Stripe customer exist. Invoice
  // mode only needs the linked Stripe customer. The confirm button renders
  // when the SELECTED method is available.
  const cardChargeAllowed =
    client.canCharge !== false ||
    (editableAmount > 0 && Boolean(pm) && Boolean(client.stripeCustomerId));
  const showConfirmButton =
    !isViewMode &&
    (isScheduleNotice
      ? canScheduleNotice
      : isSendInvoice
        ? canSendInvoice
        : cardChargeAllowed);

  // The customer already holds an invoice naming a date. Charging now is still
  // allowed — an admin may have a reason — but it bills them early, so say so
  // rather than quietly disabling the button.
  const scheduledChargeOn = !isViewMode ? client.scheduledChargeOn : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-secondary shrink-0">
          <h3 className="text-lg font-semibold text-content-primary">
            {client.invoiceStatus === "invoiced"
              ? "Invoice Details"
              : isViewMode
                ? "Charge Details"
                : "Charge Preview"}
          </h3>
          <p className="text-sm text-content-secondary mt-0.5">{monthLabel}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
          {/* Client Info + Payment Method */}
          <div className="bg-surface-secondary rounded-lg p-4 flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-content-tertiary uppercase tracking-wider font-medium">Charge To</div>
              <div className="font-semibold text-sm text-content-primary mt-1">
                {client.billingName || client.hotelName}
              </div>
              <div className="text-sm text-content-secondary mt-0.5">
                {client.stripeEmail || client.email}
              </div>
            </div>
            {pm && (
              <div className="text-right shrink-0">
                <div className="text-xs text-content-tertiary uppercase tracking-wider font-medium">Payment</div>
                <div className="text-sm font-medium capitalize mt-1">
                  {pm.brand} •••• {pm.last4}
                </div>
                <div className="text-xs text-content-tertiary mt-0.5">
                  Exp {pm.expMonth}/{pm.expYear}
                </div>
              </div>
            )}
          </div>

          {/* Charge Details */}
          <div className="space-y-2">
            <div className="flex justify-between items-center py-2 border-b border-dashed border-border-secondary">
              <span className="text-sm text-content-secondary">
                Booking Revenue ({monthLabel})
              </span>
              <span className="text-sm font-medium text-content-primary">
                {formatCurrencyPrecise(client.bookingRevenue)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-dashed border-border-secondary">
              <span className="text-sm text-content-secondary">Commission Rate</span>
              <span className="text-sm font-medium text-content-primary">{client.commissionRate}%</span>
            </div>
            <div className="pt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-content-primary font-semibold">
                  {client.invoiceStatus === "invoiced"
                    ? "Amount Invoiced"
                    : isViewMode
                      ? "Amount Charged"
                      : isSendInvoice || isScheduleNotice
                        ? "Amount to Invoice"
                        : "Amount to Charge"}
                </span>
                {/* No Edit Amount while scheduling: the notice endpoint prices
                    the period itself, so an override here would go nowhere. */}
                {!isViewMode && !isScheduleNotice && (
                  <button
                    onClick={() => {
                      // Cancelling an edit discards the override and reverts to
                      // the calculated amount, so the charge isn't flagged.
                      if (isEditing) {
                        setEditableAmountStr(initialAmount.toString());
                        setAmountWasEdited(false);
                      }
                      setIsEditing(!isEditing);
                    }}
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    {isEditing ? "Cancel Edit" : "Edit Amount"}
                  </button>
                )}
              </div>
              {isEditing && !isViewMode ? (
                <>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-content-secondary text-lg">$</span>
                    <input
                      type="text"
                      value={editableAmountStr}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, "");
                        setEditableAmountStr(val);
                        setAmountWasEdited(true);
                      }}
                      className="flex-1 px-3 py-2 border border-border-secondary rounded-lg text-lg font-semibold text-success-dark focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="0.00"
                    />
                  </div>
                  {hasAddOn && (
                    <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-amber-800">
                      The Email Marketing add-on is billed{" "}
                      <span className="font-semibold">on top</span> of this amount:
                      <div className="mt-1 font-medium text-content-primary">
                        Custom fee {formatCurrencyPrecise(editableAmount)} + Add-on{" "}
                        {formatCurrencyPrecise(addOnAmount)} ={" "}
                        <span className="text-success-dark font-bold">
                          {formatCurrencyPrecise(editableAmount + addOnAmount)}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xl font-bold text-success-dark mt-1">
                  {formatCurrencyPrecise(editableAmount)}
                </div>
              )}
            </div>
          </div>

          {/* Already promised a date — charging now takes the money early. */}
          {scheduledChargeOn && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              This period is already scheduled — the customer was told their card
              will be charged on{" "}
              <span className="font-semibold">{scheduledChargeOn}</span>.
              Charging now bills them early.
            </div>
          )}

          {/* Billing method — charge the saved card now, email the invoice and
              take the card a few days later, or email a payable Stripe invoice
              and let the customer pay it themselves. */}
          {!isViewMode && (
            <BillingMethodPicker
              action={action}
              collectionMethod={collectionMethod}
              onSelect={selectBillingMethod}
              canAutoCharge={canAutoCharge}
              canSendInvoice={canSendInvoice}
              canScheduleNotice={canScheduleNotice}
              paymentMethod={pm}
              dueDaysStr={dueDaysStr}
              onDueDaysChange={setDueDaysStr}
              isDueDaysValid={isDueDaysValid}
              noticeDaysStr={noticeDaysStr}
              onNoticeDaysChange={setNoticeDaysStr}
              isNoticeDaysValid={isNoticeDaysValid}
            />
          )}

          {/* Invoice Line Items Preview — always shown when backend returns
              any line items, so even the "honest single line" fallback
              (Lakeside case) gets a preview block instead of being hidden.
              Hidden while editing because the backend preview reflects the
              calculated amount, not the admin's override. */}
          {invoiceLines.length > 0 && !isEditing && (
            <InvoiceLinesPreview
              lines={invoiceLines}
              total={editableAmount}
              totalLabel={
                (isSendInvoice || isScheduleNotice) && !isViewMode
                  ? "Total Due"
                  : "Total Charged"
              }
            />
          )}

          {/* Rate mismatch vs plan — block + acknowledge override */}
          {hasRateMismatch && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                Rate mismatch vs plan
              </div>
              <div className="text-sm text-content-primary">
                Charged rate{" "}
                <span className="font-semibold">{client.commissionRate}%</span>
                {client.planExpectedRate != null && (
                  <>
                    {" "}
                    doesn't match the plan rate{" "}
                    <span className="font-semibold">
                      {+(client.planExpectedRate * 100).toFixed(4)}%
                    </span>
                  </>
                )}
                .
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-content-primary cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledgeRateMismatch}
                  onChange={(e) => setAcknowledgeRateMismatch(e.target.checked)}
                  className="rounded border-border-secondary"
                />
                I've reviewed this — charge anyway
              </label>
            </div>
          )}

          {/* Invoice Note — not offered while scheduling: the notice endpoint
              takes no note, so the box would collect text nobody ever reads. */}
          {!isViewMode && !isScheduleNotice && (
            <div>
              <label className="block text-sm font-medium text-content-primary mb-1.5">
                Invoice Note
                <span className="text-content-tertiary font-normal ml-1">(optional)</span>
              </label>
              <textarea
                value={invoiceNote}
                onChange={(e) => setInvoiceNote(e.target.value)}
                placeholder="Add a note that will appear on the invoice memo..."
                className="w-full px-3 py-2 text-sm border border-border-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                rows={2}
                maxLength={500}
              />
              {invoiceNote.length > 0 && (
                <div className="text-xs text-content-tertiary text-right mt-1">
                  {invoiceNote.length}/500
                </div>
              )}
            </div>
          )}

          {/* Send invoice email — opt-in for card charges (default off),
              defaulted ON for send_invoice since the email carries the pay link.
              Absent while scheduling a notice: that email IS the action, so
              there is nothing to opt into. */}
          {!isViewMode && !isScheduleNotice && (
            <label className="flex items-start gap-2 text-sm text-content-primary cursor-pointer">
              <input
                type="checkbox"
                checked={sendInvoiceEmail}
                onChange={(e) => setSendInvoiceEmail(e.target.checked)}
                className="rounded border-border-secondary mt-0.5"
              />
              <span>
                Send invoice email to customer
                <span className="block text-xs text-content-tertiary font-normal">
                  {isSendInvoice
                    ? "Recommended — the email contains the customer's payment link. Uncheck only if you'll share the link yourself (Copy Pay Link on the row)."
                    : "Off by default — the customer is charged but not emailed an invoice unless you check this."}
                </span>
              </span>
            </label>
          )}

          {/* Payment link — surfaced once the invoice is issued (send_invoice) so
              the admin can copy/share the customer's Stripe pay page from here
              instead of hunting the row's ⋮ menu. */}
          {client.invoiceStatus === "invoiced" &&
            client.stripeHostedInvoiceUrl && (
              <IssuedInvoicePanel
                url={client.stripeHostedInvoiceUrl}
                onCopy={onCopyPayLink}
              />
            )}

          {/* Status + Stripe ID */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-content-secondary">Status:</span>
              <div
                className={`${
                  STATUS_COLORS[client.invoiceStatus]
                } text-xs font-medium border rounded-md py-0.5 px-2`}
              >
                {STATUS_LABELS[client.invoiceStatus]}
              </div>
            </div>
            {client.stripeCustomerId && (
              <span className="text-xs text-content-tertiary font-mono">
                {client.stripeCustomerId}
              </span>
            )}
          </div>
          {/* Override attribution + re-charge log — view mode, only when recorded */}
          {isViewMode && <ChargeAuditPanel client={client} />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-secondary flex gap-3 justify-end shrink-0">
          {onPreviewInvoiceEmail && (
            <Button
              variant="outline"
              onClick={() =>
                onPreviewInvoiceEmail({
                  // The advance notice is the same document as a card charge's
                  // invoice — no Pay Now button — so project it that way.
                  collectionMethod: isScheduleNotice
                    ? "charge_automatically"
                    : collectionMethod,
                  daysUntilDue: isSendInvoice ? dueDays : undefined,
                })
              }
              className="mr-auto"
            >
              Preview invoice email
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {showConfirmButton && (
            <>
              {/* Anchor the tooltip on a wrapping span: a disabled button does
                  not fire hover events, so the span is the hover target. */}
              <span className="inline-flex" data-tooltip-id="charge-card-blocked">
                <Button
                  onClick={() => {
                    if (isScheduleNotice) {
                      // A different endpoint entirely — it prices the period
                      // and bills the card on file, so nothing else travels.
                      onConfirm({ action: "schedule_notice", noticeDays });
                      return;
                    }
                    onConfirm({
                      action: "charge_card",
                      customAmount:
                        amountWasEdited && editableAmount > 0
                          ? editableAmount
                          : undefined,
                      invoiceNote: invoiceNote.trim() || undefined,
                      acknowledgeRateMismatch: hasRateMismatch
                        ? acknowledgeRateMismatch
                        : undefined,
                      sendInvoiceEmail,
                      collectionMethod,
                      daysUntilDue: isSendInvoice ? dueDays : undefined,
                    });
                  }}
                  disabled={
                    isLoading ||
                    (hasRateMismatch && !acknowledgeRateMismatch) ||
                    (isSendInvoice && !isDueDaysValid) ||
                    (isScheduleNotice && !isNoticeDaysValid)
                  }
                >
                  {isLoading
                    ? "Loading..."
                    : isScheduleNotice
                      ? "Email Invoice & Schedule"
                      : isSendInvoice
                        ? "Send Invoice"
                        : "Charge Card"}
                </Button>
              </span>
              {hasRateMismatch && !acknowledgeRateMismatch && (
                <ReactTooltip
                  id="charge-card-blocked"
                  delayShow={0}
                  className="!z-[2] w-[230px] !rounded-md !border border-border-secondary !bg-surface-primary !p-2.5 !text-[11px] !text-content-tertiary !shadow-dropdown !normal-case !tracking-normal"
                  noArrow
                >
                  {isSendInvoice || isScheduleNotice ? "Billing" : "Charge"} is
                  blocked by the rate-vs-plan mismatch. Tick{" "}
                  <span className="font-semibold text-content-primary">
                    “I&apos;ve reviewed this — charge anyway”
                  </span>{" "}
                  above to enable it.
                </ReactTooltip>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
